import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Clock, FileText, Layers, Loader2, Search, TrendingUp, X } from "lucide-react";
import { http } from "@/lib/api";
import { clearHistory, pushHistory, readHistory } from "@/lib/hooks";
import { BrandLogo } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";

const EMPTY = { brands: [], categories: [], documents: [] };

const Highlight = ({ text, term }) => {
  const i = term ? text.toLowerCase().indexOf(term.toLowerCase()) : -1;
  if (i < 0) return text;
  return (<>{text.slice(0, i)}<mark className="rounded bg-ph-lime/25 px-0.5 text-ph-ink">{text.slice(i, i + term.length)}</mark>{text.slice(i + term.length)}</>);
};

function useSearch(query) {
  const [res, setRes] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const term = query.trim();
    if (!term) { setRes(EMPTY); setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      http.get("/search", { params: { q: term } })
        .then(({ data }) => alive && setRes({ brands: data?.brands ?? [], categories: data?.categories ?? [], documents: data?.documents ?? [] }))
        .catch(() => alive && setRes(EMPTY))
        .finally(() => alive && setLoading(false));
    }, 120);
    return () => { alive = false; clearTimeout(t); };
  }, [query]);
  return { res, loading };
}

const Group = ({ label, icon: Icon, children, action }) => (
  <div className="px-2 pb-2">
    <div className="flex items-center justify-between px-3 pb-1.5 pt-3">
      <p className="flex items-center gap-2 font-mono-ph text-[11px] uppercase tracking-[0.18em] text-ph-muted"><Icon className="h-3.5 w-3.5" />{label}</p>
      {action}
    </div>
    {children}
  </div>
);

export function SearchBar({ query, setQuery, inputRef, popular = [] }) {
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [history, setHistory] = useState(readHistory);
  const { res, loading } = useSearch(query);
  const term = query.trim();

  const items = useMemo(() => {
    if (!term) {
      return [...history.map((h) => ({ type: "history", key: `h-${h}`, value: h })),
        ...popular.slice(0, 5).map((b) => ({ type: "brand", key: `p-${b.id}`, value: b }))];
    }
    return [...res.brands.map((b) => ({ type: "brand", key: `b-${b.id}`, value: b })),
      ...res.categories.map((c) => ({ type: "category", key: `c-${c}`, value: c })),
      ...res.documents.map((d) => ({ type: "document", key: `d-${d.id}`, value: d }))];
  }, [term, res, history, popular]);

  useEffect(() => { setActive(-1); }, [term]);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault(); inputRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [inputRef]);

  const go = (item) => {
    if (item.type === "history") { setQuery(item.value); inputRef.current?.focus(); return; }
    if (item.type === "brand") {
      setHistory(pushHistory(item.value.name));
      http.post("/search/log", { query: term || item.value.name, brand_id: item.value.id }).catch(() => {});
      navigate(`/brands/${item.value.slug}`);
    } else if (item.type === "category") {
      navigate(`/brands?category=${encodeURIComponent(item.value)}`);
    } else {
      setHistory(pushHistory(item.value.brand_name));
      navigate(`/brands/${item.value.brand_slug}`);
    }
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
    else if (e.key === "Escape") setOpen(false);
    else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && items[active]) go(items[active]);
      else if (term && res.brands[0]) go({ type: "brand", value: res.brands[0] });
      else if (term) { setHistory(pushHistory(term)); http.post("/search/log", { query: term }).catch(() => {}); navigate(`/brands?q=${encodeURIComponent(term)}`); }
    }
  };

  const row = (item, idx, content) => (
    <button
      key={item.key}
      type="button"
      onMouseEnter={() => setActive(idx)}
      onClick={() => go(item)}
      data-testid={`search-suggestion-${item.key}`}
      className={cn("flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors", active === idx ? "bg-ph-raised" : "hover:bg-ph-raised/60")}
    >
      {content}
      <ArrowRight className={cn("ml-auto h-4 w-4 shrink-0 transition-[opacity,transform]", active === idx ? "translate-x-0 text-ph-accent opacity-100" : "-translate-x-1 opacity-0")} />
    </button>
  );

  let idx = -1;
  const next = () => { idx += 1; return idx; };
  const brandRow = (it) => row(it, next(), (<>
    <BrandLogo brand={it.value} className="h-10 w-10 rounded-xl" textClass="text-xs" />
    <span className="min-w-0">
      <span className="block truncate font-display text-base font-bold text-ph-ink"><Highlight text={it.value.name} term={term} /></span>
      <span className="block truncate text-xs text-ph-muted">{(it.value.categories ?? []).join(" · ") || "Electrical"} — {it.value.doc_count ?? 0} price lists</span>
    </span>
  </>));

  const brandItems = items.filter((i) => i.type === "brand");
  const showEmpty = term && !loading && items.length === 0;

  return (
    <div ref={boxRef} className="relative z-30 w-full" data-testid="hero-search">
      <div className={cn(
        "ph2-search-ring relative flex items-center gap-3 rounded-full border bg-ph-surface/90 py-2 pl-5 pr-2 shadow-[0_30px_80px_-30px_rgb(var(--ph-glow)/0.35)] backdrop-blur-xl transition-[border-color,box-shadow,transform] duration-300 sm:pl-7",
        open ? "scale-[1.01] border-ph-accent/50 ring-4 ring-ph-accent/15" : "border-ph-line",
      )}>
        {loading ? <Loader2 className="h-6 w-6 shrink-0 animate-spin text-ph-accent" /> : <Search className="h-6 w-6 shrink-0 text-ph-ink" />}
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search any company or brand name..."
          aria-label="Search brands"
          autoComplete="off"
          data-testid="hero-search-input"
          className="h-12 min-w-0 flex-1 bg-transparent text-base text-ph-ink outline-none placeholder:text-ph-muted sm:h-14 sm:text-xl"
        />
        {query && (
          <button type="button" onClick={() => { setQuery(""); inputRef.current?.focus(); }} data-testid="hero-search-clear-btn" aria-label="Clear search"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ph-muted transition-colors hover:bg-ph-raised hover:text-ph-ink">
            <X className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onKeyDown({ key: "Enter", preventDefault: () => {} })}
          data-testid="hero-search-submit-btn"
          className="hidden h-12 shrink-0 items-center gap-2 rounded-full bg-ph-lime px-6 text-sm font-semibold text-ph-navy transition-[box-shadow,transform] duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(182,255,92,0.45)] sm:flex sm:h-14"
        >
          Search <ArrowRight className="h-4 w-4" />
        </button>
        <button type="button" aria-label="Search" onClick={() => onKeyDown({ key: "Enter", preventDefault: () => {} })} data-testid="hero-search-submit-mobile"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ph-lime text-ph-navy sm:hidden">
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      <AnimatePresence>
        {open && (items.length > 0 || showEmpty) && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.99 }}
            transition={{ duration: 0.18 }}
            data-lenis-prevent
            data-testid="search-dropdown"
            className="absolute left-0 right-0 top-full mt-3 max-h-[min(60vh,460px)] overflow-y-auto rounded-[1.75rem] border border-ph-line bg-ph-surface text-left shadow-[0_40px_80px_-30px_rgba(16,22,37,0.6)]"
          >
            {!term && history.length > 0 && (
              <Group label="Recent searches" icon={Clock}
                action={<button type="button" data-testid="search-history-clear-btn" onClick={() => setHistory(clearHistory())} className="text-xs font-medium text-ph-muted hover:text-ph-accent">Clear</button>}>
                {items.filter((i) => i.type === "history").map((it) => row(it, next(), (<><Clock className="h-4 w-4 text-ph-muted" /><span className="text-sm text-ph-ink/85">{it.value}</span></>)))}
              </Group>
            )}
            {brandItems.length > 0 && (
              <Group label={term ? "Brands" : "Popular brands"} icon={term ? Search : TrendingUp}>{brandItems.map(brandRow)}</Group>
            )}
            {res.categories.length > 0 && term && (
              <Group label="Categories" icon={Layers}>
                {items.filter((i) => i.type === "category").map((it) => row(it, next(), (<><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ph-lime/15"><Layers className="h-4 w-4 text-ph-accent" /></span><span className="text-sm font-medium text-ph-ink"><Highlight text={it.value} term={term} /></span></>)))}
              </Group>
            )}
            {res.documents.length > 0 && term && (
              <Group label="Price lists" icon={FileText}>
                {items.filter((i) => i.type === "document").map((it) => row(it, next(), (<><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ph-violet/15"><FileText className="h-4 w-4 text-ph-violet" /></span><span className="min-w-0"><span className="block truncate text-sm font-medium text-ph-ink"><Highlight text={it.value.title} term={term} /></span><span className="block text-xs text-ph-muted">{it.value.brand_name}</span></span></>)))}
              </Group>
            )}
            {showEmpty && (
              <div className="px-6 py-8 text-center" data-testid="search-no-results">
                <p className="font-display text-lg font-bold text-ph-ink">No match for “{term}”</p>
                <p className="mt-1 text-sm text-ph-muted">Try a brand name like Polycab, or a category like Switchgear.</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
